import {
  LoginCredentials,
  RegisterData,
  AuthResponse,
  ApiResponse,
  User,
  ChangePasswordData,
  ForgotPasswordData,
  ResetPasswordData,
} from "../types/auth";
import { buildApiUrl, API_CONFIG } from "@/config/api";

const API_BASE_URL = buildApiUrl(API_CONFIG.ENDPOINTS.AUTH);

class AuthService {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private refreshPromise: Promise<void> | null = null;

  constructor() {
    this.accessToken = localStorage.getItem("accessToken");
    this.refreshToken = localStorage.getItem("refreshToken");
    this.setupTokenRefresh();
  }

  private async apiRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${API_BASE_URL}${endpoint}`;

    const defaultHeaders: HeadersInit = {
      "Content-Type": "application/json",
    };

    if (this.accessToken) {
      defaultHeaders.Authorization = `Bearer ${this.accessToken}`;
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);

      // If unauthorized and we have a refresh token, try refresh once
      if (
        response.status === 401 &&
        this.refreshToken &&
        !endpoint.includes("/refresh")
      ) {
        await this.handleTokenRefresh();

        if (this.accessToken) {
          const retryHeaders = {
            ...defaultHeaders,
            Authorization: `Bearer ${this.accessToken}`,
          };

          const retryConfig: RequestInit = {
            ...options,
            headers: {
              ...retryHeaders,
              ...options.headers,
            },
          };

          const retryResponse = await fetch(url, retryConfig);
          const retryData = await retryResponse.json();

          if (!retryResponse.ok) {
            const err: any = new Error(
              retryData?.error || retryData?.message || "Request failed"
            );
            err.status = retryResponse.status;
            err.code =
              retryData?.code ||
              retryData?.errorCode ||
              retryData?.error_code ||
              retryData?.type ||
              retryData?.error ||
              retryData?.reason ||
              null;
            err.raw = retryData;
            throw err;
          }

          return retryData;
        }
      }

      const data = await response.json();

      if (!response.ok) {
        const err: any = new Error(data?.error || data?.message || "Request failed");
        err.status = response.status;
        err.code =
          data?.code ||
          data?.errorCode ||
          data?.error_code ||
          data?.type ||
          data?.error ||
          data?.reason ||
          null;
        err.raw = data;
        throw err;
      }

      return data;
    } catch (error) {
      console.error("API request failed:", error);
      throw error;
    }
  }

  private setTokens(tokens: { accessToken: string; refreshToken: string }) {
    this.accessToken = tokens.accessToken;
    this.refreshToken = tokens.refreshToken;

    localStorage.setItem("accessToken", tokens.accessToken);
    localStorage.setItem("refreshToken", tokens.refreshToken);

    this.setupTokenRefresh();
  }

  private clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;

    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
  }

  private setUser(user: User) {
    localStorage.setItem("user", JSON.stringify(user));
  }

  getStoredUser(): User | null {
    const userData = localStorage.getItem("user");
    return userData ? JSON.parse(userData) : null;
  }

  isAuthenticated(): boolean {
    return !!this.accessToken && !!this.getStoredUser();
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  // LOGIN — unchanged
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await this.apiRequest<AuthResponse>("/login", {
      method: "POST",
      body: JSON.stringify(credentials),
    });

    if (response.success && response.data) {
      this.setTokens(response.data.tokens);
      this.setUser(response.data.user);
      return response.data;
    }

    throw new Error("Login failed");
  }

  // REGISTER — now returns pending info (no tokens yet)
  async register(
    data: RegisterData
  ): Promise<{ pendingUserId: string; email: string }> {
    const response = await this.apiRequest<{
      pendingUserId: string;
      email: string;
    }>("/register", {
      method: "POST",
      body: JSON.stringify(data),
    });

    if (response.success && response.data) {
      // do NOT set tokens here; wait for OTP verification
      return response.data;
    }

    throw new Error("Registration failed");
  }

  // NEW — verify signup OTP → returns AuthResponse (+ set tokens/user)
  async verifySignupOtp(email: string, code: string): Promise<AuthResponse> {
    const response = await this.apiRequest<AuthResponse>("/register/verify-otp", {
      method: "POST",
      body: JSON.stringify({ email, code }),
    });

    if (response.success && response.data) {
      this.setTokens(response.data.tokens);
      this.setUser(response.data.user);
      return response.data;
    }

    throw new Error("OTP verification failed");
  }

  // NEW — resend signup OTP (used for both new signups & unverified-login path)
  async resendSignupOtp(email: string): Promise<void> {
    const response = await this.apiRequest("/register/resend-otp", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
    if (!response.success) throw new Error("Failed to resend OTP");
  }

  // Refresh tokens (unchanged API)
  async refreshTokens(): Promise<boolean> {
    if (!this.refreshToken) {
      return false;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: this.refreshToken }),
      });

      if (!response.ok) throw new Error("Token refresh failed");

      const data = await response.json();

      if (data.success && data.data?.tokens) {
        this.setTokens(data.data.tokens);
        return true;
      }

      return false;
    } catch (error) {
      console.error("Token refresh failed:", error);
      this.clearTokens();
      return false;
    }
  }

  private async handleTokenRefresh(): Promise<void> {
    if (this.refreshPromise) return this.refreshPromise;

    this.refreshPromise = this.refreshTokens().then((success) => {
      this.refreshPromise = null;
      if (!success) window.location.href = "/login";
    });

    return this.refreshPromise;
  }

  private setupTokenRefresh(): void {
    if (!this.accessToken) return;

    try {
      const payload = JSON.parse(atob(this.accessToken.split(".")[1]));
      const expirationTime = payload.exp * 1000;
      const refreshTime = Math.max(
        expirationTime - Date.now() - 5 * 60 * 1000,
        0
      );

      if (refreshTime > 0) {
        setTimeout(() => {
          this.handleTokenRefresh();
        }, refreshTime);
      }
    } catch (error) {
      console.error("Error setting up token refresh:", error);
    }
  }

  // LOGOUT — unchanged
  async logout(): Promise<void> {
    try {
      await this.apiRequest("/logout", { method: "POST" });
    } catch (error) {
      console.error("Logout API call failed:", error);
    } finally {
      this.clearTokens();
    }
  }

  // refresh access (legacy helper)
  async refreshAccessToken(): Promise<void> {
    if (!this.refreshToken) throw new Error("No refresh token available");

    try {
      const response = await this.apiRequest<{
        accessToken: string;
        expiresIn: string;
      }>("/refresh", {
        method: "POST",
        body: JSON.stringify({ refreshToken: this.refreshToken }),
      });

      if (response.success && response.data) {
        this.accessToken = response.data.accessToken;
        localStorage.setItem("accessToken", response.data.accessToken);
      } else {
        throw new Error("Token refresh failed");
      }
    } catch (error) {
      console.error("Token refresh failed:", error);
      this.clearTokens();
      throw error;
    }
  }

  // PROFILE — unchanged
  async getCurrentUser(): Promise<User> {
    const response = await this.apiRequest<User>("/me", { method: "GET" });

    if (response.success && response.data) {
      this.setUser(response.data);
      return response.data;
    }

    throw new Error("Failed to get user profile");
  }

  async updateProfile(data: Partial<User>): Promise<User> {
    const response = await this.apiRequest<User>("/me", {
      method: "PUT",
      body: JSON.stringify(data),
    });

    if (response.success && response.data) {
      this.setUser(response.data);
      return response.data;
    }

    throw new Error("Failed to update profile");
  }

  async changePassword(data: ChangePasswordData): Promise<void> {
    const response = await this.apiRequest("/change-password", {
      method: "POST",
      body: JSON.stringify(data),
    });

    if (!response.success) throw new Error("Failed to change password");
  }

  // OLD link-based forgot/reset kept for compatibility (not used now)
  async forgotPassword(_data: ForgotPasswordData): Promise<void> {
    const response = await this.apiRequest("/forgot-password", {
      method: "POST",
      body: JSON.stringify(_data),
    });
    if (!response.success) throw new Error("Failed to send reset email");
  }

  async resetPassword(_data: ResetPasswordData): Promise<void> {
    const response = await this.apiRequest("/reset-password", {
      method: "POST",
      body: JSON.stringify(_data),
    });
    if (!response.success) throw new Error("Failed to reset password");
  }

  // NEW — OTP forgot/reset
  async forgotPasswordOtp(email: string): Promise<void> {
    const response = await this.apiRequest("/forgot-password/otp", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
    if (!response.success) throw new Error("Failed to send reset code");
  }

  async resetPasswordWithOtp(
    email: string,
    code: string,
    password: string
  ): Promise<void> {
    const response = await this.apiRequest("/reset-password/otp", {
      method: "POST",
      body: JSON.stringify({ email, code, password }),
    });
    if (!response.success) throw new Error("Failed to reset password");
  }

  // Logout all devices — unchanged
  async logoutAll(): Promise<void> {
    try {
      await this.apiRequest("/logout-all", { method: "POST" });
    } catch (error) {
      console.error("Logout all API call failed:", error);
    } finally {
      this.clearTokens();
    }
  }
async precheckLogin(email: string): Promise<"ok" | "unverified" | "not_found"> {
  const resp = await this.apiRequest<{ status: "ok" | "unverified" | "not_found" }>(
    "/login/precheck",
    { method: "POST", body: JSON.stringify({ email }) }
  );
  if (resp?.success && resp.data?.status) return resp.data.status;
  return "ok";
}

}

export const authService = new AuthService();
export default authService;
